import React, { useRef, useState, useEffect } from 'react';
import { getCurrencyDisplay, CURRENCIES } from '../config/currencies';
import { useFavoriteCurrencies } from '../hooks/useFavoriteCurrencies';

interface CurrencySelectProps {
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
  className?: string;
}

const ALL_CURRENCIES = Object.keys(CURRENCIES);

const CurrencySelect: React.FC<CurrencySelectProps> = ({
  value,
  onChange,
  disabled = false,
  className = 'form-select',
}) => {
  const { favorites, toggleFavorite, isFavorite } = useFavoriteCurrencies();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const favList = ALL_CURRENCIES.filter(c => favorites.includes(c));
  const otherList = ALL_CURRENCIES.filter(c => !favorites.includes(c));

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={className}
        onClick={() => !disabled && setOpen(o => !o)}
        disabled={disabled}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: disabled ? 'not-allowed' : 'pointer',
          textAlign: 'left',
          width: '100%',
        }}
      >
        <span>{getCurrencyDisplay(value)}</span>
        <span style={{ fontSize: 10, marginLeft: 4, color: 'var(--text-secondary)' }}>▾</span>
      </button>

      {open && (
        <div style={{
          position: 'absolute',
          zIndex: 1000,
          top: 'calc(100% + 4px)',
          left: 0,
          right: 0,
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-small)',
          boxShadow: 'var(--shadow)',
          maxHeight: 300,
          overflowY: 'auto',
        }}>
          {favList.length > 0 && (
            <>
              <div style={{
                padding: '4px 12px',
                fontSize: 11,
                fontWeight: 600,
                color: 'var(--text-secondary)',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                background: 'var(--background)',
              }}>
                Favorites
              </div>
              {favList.map(code => (
                <CurrencyOption
                  key={code}
                  code={code}
                  selected={value === code}
                  isFavorite={true}
                  onSelect={() => { onChange(code); setOpen(false); }}
                  onToggleFavorite={() => toggleFavorite(code)}
                />
              ))}
              <div style={{ borderTop: '1px solid var(--border)', margin: '2px 0' }} />
            </>
          )}
          {otherList.map(code => (
            <CurrencyOption
              key={code}
              code={code}
              selected={value === code}
              isFavorite={false}
              onSelect={() => { onChange(code); setOpen(false); }}
              onToggleFavorite={() => toggleFavorite(code)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

interface CurrencyOptionProps {
  code: string;
  selected: boolean;
  isFavorite: boolean;
  onSelect: () => void;
  onToggleFavorite: () => void;
}

const CurrencyOption: React.FC<CurrencyOptionProps> = ({
  code, selected, isFavorite, onSelect, onToggleFavorite,
}) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '7px 12px',
      cursor: 'pointer',
      background: selected ? 'var(--primary-light)' : 'transparent',
      color: selected ? 'var(--primary)' : 'var(--text-primary)',
      fontWeight: selected ? 600 : 400,
      fontSize: 14,
    }}
    onMouseEnter={e => { if (!selected) (e.currentTarget as HTMLDivElement).style.background = 'var(--background)'; }}
    onMouseLeave={e => { if (!selected) (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
  >
    <span onClick={onSelect} style={{ flex: 1 }}>
      {getCurrencyDisplay(code)}
    </span>
    <span
      onClick={e => { e.stopPropagation(); onToggleFavorite(); }}
      title={isFavorite ? 'Remove from favorites' : 'Add to favorites'}
      style={{
        fontSize: 14,
        color: isFavorite ? '#f5a623' : 'var(--text-muted)',
        cursor: 'pointer',
        padding: '0 4px',
        lineHeight: 1,
      }}
    >
      {isFavorite ? '★' : '☆'}
    </span>
  </div>
);

export default CurrencySelect;
